import { GridReadyEvent } from 'ag-grid-community';
import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { WatchAlert } from '@compumark/order-management-client';
import { Observable, Subscription } from 'rxjs';

import { AlertGridOptionsService } from '../alert-columns/alert-column-definition/alert-grid-options.service';
import { PanelViewRowRendererComponent } from '../alert-columns/panel-view-row-renderer/panel-view-row-renderer.component';
import { AlertApiClientService } from '../services/alert-api-client.service';
import { AlertStateService } from '../services/alert-state.service';

@Component({
  selector: 'app-alert-grid',
  templateUrl: './alert-grid.component.html',
  styleUrls: ['./alert-grid.component.scss'],
  standalone: false,
})
export class AlertGridComponent implements OnInit, OnDestroy {
  private alertOptionsService = inject(AlertGridOptionsService);
  private alertsService = inject(AlertApiClientService);
  private alertStateService = inject(AlertStateService);

  public gridOptions: any;
  public tableRowData!: Observable<WatchAlert[]>;
  public newAlertId: string | null = null;
  public alertsDisabled = signal<boolean>(false);
  private refreshSubscription?: Subscription;

  constructor() {
    this.gridOptions = this.alertOptionsService.gridOptions;
    this.gridOptions.suppressContextMenu = true;
    this.gridOptions.components = {
      panelViewRowRenderer: PanelViewRowRendererComponent,
    };
  }

  ngOnInit(): void {
    this.tableRowData = this.alertsService.getAlerts();

    this.alertStateService.disableAll$.subscribe((state) => {
      this.alertsDisabled.set(state);
    });

    this.refreshSubscription = this.alertOptionsService
      .getRefreshObservable()
      .subscribe(() => {
        this.refreshGrid();
      });
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  private refreshGrid(): void {
    const gridApi = this.alertOptionsService.getGridApi();
    if (!gridApi) {
      this.tableRowData = this.alertsService.getAlerts();
      return;
    }

    const expandedAlertIds: Set<string> = new Set();
    const panelRowMap: Map<string, any> = new Map();
    const masterStateMap: Map<string, any> = new Map();

    gridApi.forEachNode((node: any) => {
      if (!node.data) {
        return;
      }

      if (node.data._panelRow) {
        const parentId = node.data.id.replace('__panel', '');
        panelRowMap.set(parentId, {
          ...node.data,
          id: node.data.id.endsWith('__panel')
            ? node.data.id
            : node.data.id + '__panel',
        });
      } else {
        masterStateMap.set(node.data.id, {
          _panelRowFetched: node.data._panelRowFetched,
          _panelOpen: node.data._panelOpen,
        });

        const panelId = node.data.id + '__panel';
        const panelNode = gridApi.getRowNode(panelId);
        if (panelNode) {
          expandedAlertIds.add(node.data.id);
        }
      }
    });

    this.alertsService.getAlerts().subscribe((alerts) => {
      const newRowData: any[] = [];

      alerts.forEach((alert) => {
        const alertId = alert.id;
        const alertClone: any = { ...alert };
        if (alertId) {
          const savedState = masterStateMap.get(alertId);
          if (savedState?._panelRowFetched) {
            alertClone._panelRowFetched = true;
          }
          if (savedState?._panelOpen) {
            alertClone._panelOpen = true;
          }
        }

        newRowData.push(alertClone);

        if (!alertId) {
          return;
        }

        if (expandedAlertIds.has(alertId)) {
          const existingPanelRow = panelRowMap.get(alertId);
          if (existingPanelRow) {
            newRowData.push(existingPanelRow);
          } else {
            newRowData.push({
              id: `${alertId}__panel`,
              _panelRow: true,
            });
          }
        }
      });

      gridApi.setGridOption('rowData', newRowData);

      if (expandedAlertIds.size > 0) {
        setTimeout(() => {
          expandedAlertIds.forEach((alertId) => {
            const masterNode = gridApi.getRowNode(alertId);
            if (masterNode && masterNode.data) {
              gridApi.redrawRows({ rowNodes: [masterNode] });
            }
          });
        }, 100);
      }
    });
  }

  onGridReady(event: GridReadyEvent): void {
    this.alertOptionsService.setGridApi(event.api);
    //this.alertOptionsService.setCol
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const gridApi = this.alertOptionsService.getGridApi();
    if (gridApi) {
      setTimeout(() => {
        gridApi.redrawRows();
      }, 10);
    }
  }

  onOverlayWheel(event: WheelEvent): void {
    event.preventDefault();
    const gridApi = this.alertOptionsService.getGridApi();
    if (gridApi) {
      const gridBodyViewport = document.querySelector(
        '.ag-body-viewport',
      ) as HTMLElement;
      if (gridBodyViewport) {
        gridBodyViewport.scrollTop += event.deltaY;
      }
    }
  }
}
